/***********************************************************************
 An instance of this class handles multiple sco's and act to the LMS
 as a single SCO with the given Scorm 1.2 API.
 The stored data will be saved compresses and encrypted in the
 suspend data field on LMS.
 ***********************************************************************/
import {ScoData} from "scorm/scorm_1_2/ScoData";
import {Sco} from "scorm/common/Sco";
import {ScoRegistry} from "scorm/common/ScoRegistry";
import {SequenceDataMap} from "course/SequenceDataMap";
import {Event} from "common/Event";
import {CourseEvents} from "course/CourseEvents";
import {LoggingService} from "logging/LoggingService";
import {Assert} from "common/Assert";
import {AiccRequestService} from "./AiccRequestService";
import {ScormUtils} from "../scorm/common/ScormUtils";
import {buildCommitData} from "./converter/ScormToAiccConverter";
import {initializeScormData} from "./converter/AiccToScormConverter";


export function AiccCourse(aiccUrl, aiccSID, aiccProxy, defaultMasteryScore = 80 ) {

    const aiccRequestService = new AiccRequestService(aiccUrl, aiccSID, aiccProxy),
        scoRegistry = new ScoRegistry( ScoData ),
        domStructure = new SequenceDataMap();

    let activeSco = undefined,
        startTime = new Date(),
        username = "",
        courseScoData;

    function initializeAiccSession() {
        const lmsResponse = aiccRequestService.getParam();

        if (!lmsResponse) {
            return "false";
        }

        courseScoData = initializeScormData(lmsResponse, defaultMasteryScore);
    }

    async function commit() {
        courseScoData.refreshStatus(scoRegistry.getStatus(), scoRegistry.collectSuspendData(), getLapsedTime());

        if ( !courseScoData.checkSuspendDataSize() ) {
            LoggingService.warning( "You are connected to an AICC LMS and the suspend_data is longer than 4096 bytes!" );
        }

        if (!courseScoData) {
            return Promise.resolve();
        }

        refreshUI();

        const aiccData = buildCommitData(courseScoData);
        return aiccRequestService.putParam(aiccData)
            .catch(error => LoggingService.error(error));
    }

    function terminateAiccSession() {
        aiccRequestService.exitAU()
            .catch( () => LoggingService.error("ERROR on ExitAU: Request failed") );
    }

    function refreshUI() {
        Event.trigger(document, CourseEvents.BUFFER_REFRESHED);
    }

    async function terminateActiveLearningSco() {
        if( !activeSco ) {
            return Promise.resolve();
        }

        const changedData = activeSco.getChangedData();
        for (const [key, value] of changedData.entries()) {
            scoRegistry.setValue( activeSco.getId(), key, value );
        }

        Event.trigger(document, CourseEvents.SEQUENCE_TERMINATED, {scoId: activeSco.getId()});
        activeSco = undefined;

        return commit();
    }

    function getLapsedTime() {
        return ScormUtils.getLapsedTime( startTime );
    }

    // revealing public api
    return {
        domStructure : domStructure,
        username: username,

        /***********************************************************************
         * initializes the course by getting initial values from lms.
         ***********************************************************************/
        initialize: () => {
            initializeAiccSession();
            scoRegistry.initialize( courseScoData.get( "cmi.suspend_data" ));
        },

        /***********************************************************************
         * gets the course mastery score
         * @return {Number} mastery score
         ***********************************************************************/
        getMasteryScore: () => Number( courseScoData.get("cmi.student_data.mastery_score") ),

        /***********************************************************************
         * gets the completion threshold. What is the mastery score for scorm 1.2.
         * @return {Number} mastery score
         ***********************************************************************/
        getCompletionThreshold: () => Number( courseScoData.get("cmi.student_data.mastery_score") ),

        /***********************************************************************
         * Calculates the current course score and status and commit all updated
         * values to the lms.
         * @return {String} true / false
         ***********************************************************************/
        commit: () => {
            commit();
            return "true";
        },

        /***********************************************************************
         * sets the list for the scoring relevant scos
         * @param {boolean} useAssessments
         * @return {String} true
         ***********************************************************************/
        setScoreList: (useAssessments) => {
            domStructure.initScoreIDs(useAssessments);
            scoRegistry.setRelevantScoList( domStructure.getScoreIDs() );
            return "true";
        },

        /***********************************************************************
         * gets a value for a learning sco or the course sco
         * @param {String} scoId the id of the sco. can be null to get a value from the lms
         * @param {String} dataModelId scorm data model id for the the value
         * @return {String} value or an empty string, if the dataModelId is not defined
         ***********************************************************************/
        getValueFromCourseOrLearningSco: (scoId, dataModelId) => {
            if( scoId === null ) {
                return courseScoData.get( dataModelId );
            }

            if( scoRegistry.contains( scoId ) ) {
                return scoRegistry.getValue( scoId, dataModelId );
            }

            return "";
        },

        /***********************************************************************
         * gets a value for the course
         * @param {String} dataModelId scorm data model id for the the value
         * @return {String} value or an empty string, if the does not exist
         ***********************************************************************/
        getValue : ( dataModelId ) => {
            Assert.checkArgument( dataModelId );

            if( !courseScoData.isValid( dataModelId ) ) {
                return undefined;
            }

            return courseScoData.get( dataModelId );
        },

        /***********************************************************************
         * gets a value for the course
         * @param {String} dataModelId scorm data model id for the the value
         * @param {String} value value
         * @return {String} true / false
         ***********************************************************************/
        setValue : ( dataModelId, value ) => {
            Assert.checkArgument( dataModelId );

            if( !courseScoData.isValid( dataModelId ) ) {
                return "false";
            }

            return courseScoData.set( dataModelId, value ) ? "true" : "false";
        },

        /***********************************************************************
         * gets a value for a learning sco
         * @param {String} scoId the id of the sco. can be null to get a value from the lms
         * @param {String} dataModelId scorm data model id for the the value
         * @return {String} value or undefined
         ***********************************************************************/
        getLearningScoValue: (scoId, dataModelId) => {
            if( scoRegistry.contains( scoId ) ) {
                return scoRegistry.getValue( scoId, dataModelId );
            }

            return undefined;
        },

        terminate: function() {
            if( activeSco ) {
                terminateActiveLearningSco()
                    .then(() => terminateAiccSession());
            } else {
                terminateAiccSession();
            }
            return "true";
        },

        initializeActiveLearningSco: ( scoId ) => {
            if( !scoId || activeSco ) {
                return false;
            }

            if( !scoRegistry.contains(scoId) ) {
                scoRegistry.initializeNewSco( scoId );
                scoRegistry.setValue( scoId, "lesson_id", scoId );
            }

            const scoData = scoRegistry.get( scoId );
            activeSco = new Sco( scoId, scoData );

            return true;
        },
        hasActiveLearningSco: () => !!activeSco,
        getActiveLearningSco: () => activeSco,
        terminateActiveLearningSco: terminateActiveLearningSco,
        onDomAndModelSynchronized: refreshUI
    };
}
